import { useEffect, useState } from "react";
import { fileUrl } from "@/lib/api";
import { getFilms, saveFilm, deleteFilm } from "@/lib/cloudApi";
import CloudImagePicker from "@/components/admin/CloudImagePicker";
import { toast } from "sonner";
import { AlertCircle, Plus, Save, X } from "lucide-react";
import { getYouTubeEmbedUrl } from "@/lib/youtube";


const empty = { couple: "", location: "", thumbnail: "", url: "", length: "", order: 0 };

export default function AdminFilms() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const data = await getFilms(); setItems(Array.isArray(data)?data:[]); };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const url = String(editing.url || "").trim();
      if (!getYouTubeEmbedUrl(url)) {
        toast.error("Enter a valid YouTube video URL.");
        return;
      }

      await saveFilm({ ...editing, url, order: Number(editing.order) || 0 });
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.message || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this film?")) return;
    await deleteFilm(id);
    toast.success("Deleted");
    load();
  };

  const input = "w-full bg-transparent border-b border-[#C99A45]/30 focus:border-[#C99A45] outline-none py-2 text-sm";
  const editingVideoUrl = String(editing?.url || "").trim();
  const editingEmbedUrl = getYouTubeEmbedUrl(editingVideoUrl);
  const editingVideoInvalid = Boolean(editingVideoUrl) && !editingEmbedUrl;

  return (
    <div className="p-10" data-testid="admin-films">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow text-[#C99A45]">Manage</div>
          <h1 className="font-serif-display text-5xl mt-3">Films</h1>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="btn-gold" data-testid="add-film">
          <Plus size={14} /> Add Film
        </button>
      </div>
      <div className="gold-divider my-6"></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        {items.map((f) => (
          <div key={f.id} className="border border-[#C99A45]/20 p-4 flex gap-4" data-testid={`film-${f.id}`}>
            <div className="w-32 h-24 bg-[#151515] overflow-hidden flex-shrink-0">
              {f.thumbnail && <img src={fileUrl(f.thumbnail)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1">
              <div className="font-serif-display text-xl">{f.couple}</div>
              <div className="eyebrow text-[10px] text-[#C99A45]/80">{f.location} · {f.length}</div>
              <div className="eyebrow text-[9px] text-[#F7F1E8]/50 mt-1 truncate">{f.url}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(f)} className="text-xs text-[#C99A45] hover:underline" data-testid={`edit-film-${f.id}`}>Edit</button>
                <button onClick={() => del(f.id)} className="text-xs text-[#E52717] hover:underline" data-testid={`del-film-${f.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-[#C99A45]/30 p-8 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto" data-testid="film-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-serif-display text-2xl">{editing.id ? "Edit" : "Add"} Film</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80">Couple *</label>
              <input required value={editing.couple} onChange={(e) => setEditing({ ...editing, couple: e.target.value })} className={input} />
            </div>
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80">Location *</label>
              <input required value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className={input} />
            </div>
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80">YouTube URL *</label>
              <input
                required
                value={editing.url || ""}
                onChange={(e) => setEditing({ ...editing, url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                className={input}
              />
              <p className="mt-2 text-[11px] text-[#F7F1E8]/45">
                YouTube watch, youtu.be, shorts, and embed links are converted automatically.
              </p>
              {editingVideoInvalid && (
                <div className="mt-3 flex gap-2 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                  <AlertCircle size={14} className="mt-[1px] flex-shrink-0" />
                  <span>Invalid YouTube URL. Please paste a YouTube video, shorts, youtu.be, or embed link.</span>
                </div>
              )}
              {editingEmbedUrl && (
                <div className="mt-4 overflow-hidden rounded border border-[#C99A45]/25 bg-black">
                  <iframe
                    src={editingEmbedUrl}
                    title="Film video preview"
                    className="w-full aspect-video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="eyebrow text-[10px] text-[#C99A45]/80">Length</label>
                <input value={editing.length} onChange={(e) => setEditing({ ...editing, length: e.target.value })} placeholder="4:32" className={input} />
              </div>
              <div>
                <label className="eyebrow text-[10px] text-[#C99A45]/80">Order</label>
                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className={input} />
              </div>
            </div>
            <CloudImagePicker value={editing.thumbnail} onChange={(v) => setEditing({ ...editing, thumbnail: v })} label="Thumbnail *" testId="f-thumb" />
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-gold" data-testid="film-save"><Save size={12} /> Save</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
